import React, { useMemo } from "react";
import { ROLE, SUBSCRIPTION_TIER } from "@highnote/server/src/core/entities";
import { DialogSection } from "App/common/Dialog";
import { usePlanLimitsContext } from "App/common/PlanLimits/usePlanLimits";
import {
  CONTROLLED_SHARE_FEATURES,
  ControlledFeatureCallout,
} from "App/core/ControlledFeatureCallout";
import { InviteByLink } from "../Members/InviteByLink";
import { useShareDialogContext } from "../ShareDialogContext";
import { useAuth } from "App/components/Auth";
import { getEntityShareKeys } from "@highnote/server/src/core/shared-util";
import { useMembershipFlags } from "App/components/MembershipFlagsContext";
import { useSpaceAccessMembers } from "App/components/useEntities/useSpaceAccessMembers";

export const ShareDialogLink = () => {
  const { user } = useAuth();
  const { showPlanPickerDialog } = usePlanLimitsContext();
  const { isEntityOwner, entity, canManageDownloadControl, canManageEntity } =
    useShareDialogContext();

  /*
   * The share-link read, with the same treatment as `InviteByLink`. This
   * is the second of the three sites that derive the same share state from
   * `entity.rolesV2`.
   *
   * `useSpaceAccessMembers` attaches one listener per (spaceId, canManage,
   * enabled) triple, and `InviteByLink`, which this component renders,
   * asks for the same one. Two listeners on the same query is what
   * Firestore's client-side target de-duplication is for; it is not two
   * subscriptions on the wire.
   */
  const { membersV2Enabled } = useMembershipFlags();
  const access = useSpaceAccessMembers({
    spaceId: entity?.id,
    canManage: canManageEntity,
    enabled: membersV2Enabled,
  });
  const useAccessDocs =
    membersV2Enabled && canManageEntity && !access.loading && !access.error;

  const [legacyShareKey] = getEntityShareKeys(entity);
  const shareRoles = useAccessDocs
    ? access.shareMember?.effectiveRoles || []
    : (entity?.rolesV2 || {})[legacyShareKey] || [];

  const isShared = shareRoles.includes(ROLE.VIEW);

  const downloadToggleable =
    isShared && shareRoles.includes(ROLE.DOWNLOAD)
      ? canManageDownloadControl
      : true;

  const calloutMessage = useMemo(() => {
    if (user.subscriptionTier === SUBSCRIPTION_TIER.FREE) {
      return "Enable more security when sharing";
    }

    if (user.subscriptionTier === SUBSCRIPTION_TIER.INDIE) {
      return "Add more security when sharing. Require passwords, accounts, + more.";
    }

    return undefined;
  }, [user.subscriptionTier]);

  return (
    <div className="ShareDialogMembers">
      <DialogSection>
        <InviteByLink />
      </DialogSection>
      {isEntityOwner && (
        <ControlledFeatureCallout
          featuresStatus={{
            [CONTROLLED_SHARE_FEATURES.DOWNLOAD]: {
              enabled: downloadToggleable,
            },
            [CONTROLLED_SHARE_FEATURES.PASSWORD]: {
              enabled:
                canManageEntity &&
                user.subscriptionTier !== SUBSCRIPTION_TIER.FREE,
              ...(!downloadToggleable && {
                label: "passwords",
              }),
            },
          }}
          title="Advanced Security"
          fullMessage={calloutMessage}
          onUpgrade={showPlanPickerDialog}
          theme="monetization"
        />
      )}
    </div>
  );
};
